import React, { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { tasksAtom } from '../../../../../atoms/v0/taskAtoms';
import { componentThemeAtom } from '../../../../../atoms/v3/clockModeAtoms';
import { isThisWeek, startOfWeek, addDays, format } from 'date-fns';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import { ComponentTitle } from './ComponentTitle';

interface WeeklyEfficiencyAreaProps {
  currentTime: Date;
}

/**
 * 本周效率组件
 */
export const WeeklyEfficiencyArea: React.FC<WeeklyEfficiencyAreaProps> = ({
  currentTime
}) => {
  const { t } = useTranslation(['v3/clock']);
  const tasks = useAtomValue(tasksAtom);
  const componentTheme = useAtomValue(componentThemeAtom);

  // 计算本周任务统计
  const weeklyStats = useMemo(() => {
    const weeklyTasks = tasks.filter(task => {
      // 检查任务是否属于本周
      if (task.startAt && isThisWeek(new Date(task.startAt))) return true;
      if (task.doneAt && isThisWeek(new Date(task.doneAt))) return true;
      if (task.dueAt && isThisWeek(new Date(task.dueAt))) return true;
      return false;
    });

    const completedTasks = weeklyTasks.filter(task => task.done);
    const totalTasks = weeklyTasks.length;
    const completionRate = totalTasks > 0 ? Math.round((completedTasks.length / totalTasks) * 100) : 0;

    // 计算总时长（已完成任务的实际用时）
    const totalTimeSpent = completedTasks.reduce((total, task) => {
      if (task.startAt && task.doneAt) {
        const startTime = new Date(task.startAt);
        const endTime = new Date(task.doneAt);
        return total + Math.max(0, Math.floor((endTime.getTime() - startTime.getTime()) / 1000));
      }
      return total;
    }, 0);

    // 计算平均每日完成任务数
    const daysInWeek = 7;
    const avgTasksPerDay = totalTasks > 0 ? (completedTasks.length / daysInWeek).toFixed(1) : '0';

    return {
      total: totalTasks,
      completed: completedTasks.length,
      completionRate,
      totalTimeSpent,
      avgTasksPerDay,
    };
  }, [tasks, currentTime]);

  // 计算本周每日进度数据
  const weeklyProgressData = useMemo(() => {
    const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 }); // 周一开始

    const data = [];
    for (let day = 0; day < 7; day++) {
      const dayDate = addDays(weekStart, day);

      const completedOnThisDay = tasks.filter(task => {
        if (!task.doneAt) return false;
        const doneDate = new Date(task.doneAt);
        return doneDate.toDateString() === dayDate.toDateString();
      }).length;

      data.push({
        name: format(dayDate, 'EEE'),
        completed: completedOnThisDay,
        day
      });
    }

    return data;
  }, [tasks]);

  // 格式化时间显示
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  // 根据主题获取颜色
  const getThemeColors = () => {
    if (componentTheme === 'dark') {
      return {
        gridStroke: '#3A3A3C',
        tickFill: '#8E8E93',
        tooltipBg: '#2C2C2E',
        tooltipText: '#FFFFFF'
      };
    } else {
      return {
        gridStroke: '#E5E5EA',
        tickFill: '#AEAEB2',
        tooltipBg: '#FFFFFF',
        tooltipText: '#000000'
      };
    }
  };

  const themeColors = getThemeColors();

  return (
    <div className="h-full w-full flex flex-col p-2">
      {/* 统一标题 */}
      <ComponentTitle
        title={t('v3/clock:components.weeklyEfficiency.title', '本周效率')}
        subtitle={`${weeklyStats.completionRate}% · ${weeklyStats.completed}/${weeklyStats.total} · ${weeklyStats.avgTasksPerDay}/日`}
      />

      {/* 图表 */}
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={weeklyProgressData}
            margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
          >
            <defs>
              <linearGradient id="colorWeeklyCompleted" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke={themeColors.gridStroke}
              opacity={0.3}
            />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 8, fill: themeColors.tickFill }}
            />
            <YAxis hide={true} />
            <Tooltip
              contentStyle={{
                backgroundColor: themeColors.tooltipBg,
                border: 'none',
                borderRadius: '0.25rem',
                color: themeColors.tooltipText,
                fontSize: '0.625rem',
                padding: '0.25rem',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              }}
              labelFormatter={(label) => `${label}`}
            />
            <Area
              type="monotone"
              dataKey="completed"
              stroke="#8B5CF6"
              strokeWidth={1.5}
              fillOpacity={1}
              fill="url(#colorWeeklyCompleted)"
              activeDot={{ r: 2, fill: "#8B5CF6", stroke: "white", strokeWidth: 1 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default WeeklyEfficiencyArea;
